import React, { useState } from 'react';
import { Formik, FormikProps } from 'formik';

import CompanyName from '../../form/fields/hubspot/company-name';
import ContactFirstName from '../fields/hubspot/contact-first-name';
import ContactLastName from '../fields/hubspot/contact-last-name';
import ContactEmail from '../../form/fields/hubspot/contact-email';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { useSharePanelStore } from 'src/common/store/share.store';
import HubspotIntegrationIcon from 'src/monet/icons/HubspotIntegrationIcon';
import { apiCreateMergeFields, apiHubSpotSync } from 'src/utils/journeyApi';
import { MagicStrongIcon } from 'src/monet/icons';
import { useCurrentSubscription } from 'src/store/subscription';
import OrganizationLink from 'src/common/OrganizationLink';

type HubSpotProps = {
  hubspot_contact_first_name: string;
  hubspot_contact_last_name: string;
  hubspot_company_name: string;
  hubspot_contact_email: string;
  hubspot_sync: boolean;
};
const ShareHubSpotFormPanel = () => {
  const activeAlias = useSharePanelStore((state) => state.activeAlias)!;
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const [formDisabled, setFormDisabled] = useState(activeAlias?.hubspot_sync || false);
  const { isPro, isEnterprise, isOnTrial } = useCurrentSubscription((state) => state);
  const onJourneyShareClose = useSharePanelStore((state) => state.onJourneyShareClose);

  if (!activeAlias) {
    return null;
  }

  const handleCreateMergeFields = async (values: HubSpotProps) => {
    try {
      setFormDisabled(true);
      const response = await apiCreateMergeFields(activeAlias.uuid, values);
      if (!response?.success) {
        throw new Error('Creating merge fields failed');
      }
      setSuccessNotification('Merge fields created successfully');
    } catch (e) {
      setErrorNotification('Creating merge fields failed');
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <div className='flex flex-col'>
      <Formik
        initialValues={{
          hubspot_contact_first_name: activeAlias.contact_first_name || '',
          hubspot_contact_last_name: activeAlias.contact_last_name || '',
          hubspot_company_name: activeAlias.company_name || '',
          hubspot_contact_email: activeAlias.contact_email || '',
          hubspot_sync: activeAlias.hubspot_sync || false,
        }}
        onSubmit={async (values, actions) => {
          try {
            setFormDisabled(true);

            if (values?.hubspot_sync) {
              setSuccessNotification('HubSpot integration is already synced');
              return;
            }

            const data = {
              contact_first_name: values.hubspot_contact_first_name,
              contact_last_name: values.hubspot_contact_last_name,
              company_name: values.hubspot_company_name,
              contact_email: values.hubspot_contact_email,
            };

            await apiHubSpotSync(activeAlias.uuid, data).then((response) => {
              if (!response?.success) {
                throw new Error('HubSpot integration failed');
              }

              setSuccessNotification('HubSpot integration successful');
              actions.setFieldValue('hubspot_sync', true);
            });
          } catch (e) {
            setFormDisabled(false);
            setErrorNotification('HubSpot integration failed');
          }
        }}
      >
        {(props: FormikProps<HubSpotProps>) => (
          <form className='py-6' onSubmit={props.handleSubmit}>
            <ContactFirstName
              value={props.values.hubspot_contact_first_name}
              onChange={props.handleChange}
              mergeField={'first_name'}
              disabled={formDisabled}
            />
            <ContactLastName
              value={props.values.hubspot_contact_last_name}
              onChange={props.handleChange}
              mergeField={'last_name'}
              disabled={formDisabled}
            />
            <CompanyName
              value={props.values.hubspot_company_name}
              onChange={props.handleChange}
              mergeField={'company_name'}
              disabled={formDisabled}
            />
            <ContactEmail
              value={props.values.hubspot_contact_email}
              onChange={props.handleChange}
              mergeField={'email'}
              disabled={formDisabled}
            />
            <div className='flex flex-col space-y-2 pb-6 border-b'>
              { !isEnterprise && !isOnTrial ? (
                <>
                  <div className='text-sm'>
                    Upgrade to Grow plan plan and above to create merge fields using which you can personalize journeys
                  </div>

                  <OrganizationLink
                    onClick={() => onJourneyShareClose()}
                    className='flex justify-center mx-auto my-4'
                    to='/billing'
                  >
                    <button className='btn-bedrock-primary self-start flex space-x-1'>
                      <span>Upgrade to Grow plan</span>
                    </button>
                  </OrganizationLink>
                </>
              ) : (
                <button
                  className={
                    'border-r-0 flex justify-center items-center space-x-xs btn w-full bg-bedrock-light-gray border border-gray-700' +
                    (formDisabled ? ' disabled:opacity-50' : '')
                  }
                  disabled={formDisabled}
                  onClick={() => handleCreateMergeFields(props.values)}
                >
                  <MagicStrongIcon className='w-4 h-4' />
                  <span className='text-bedrock-p-strong'>Create Merge Fields</span>
                </button>
              )}

              <p className='my-2 text-center'>OR</p>

              <button
                type='submit'
                className={
                  'border-r-0 flex justify-center items-center space-x-xs btn w-full border border-gray-700' +
                  (formDisabled ? ' disabled:opacity-50' : '')
                }
                disabled={formDisabled}
              >
                <HubspotIntegrationIcon className='w-4 h-4' />
                <span className='text-bedrock-p-strong'>HubSpot {props.values.hubspot_sync ? 'Synced' : ''}</span>
              </button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default ShareHubSpotFormPanel;
